export const SETTINGS_KEY = "ALL_PRODUCTS_CATEGORY_MAPPING";

export const getProductUniqueId = (product) => product.variantId;

export const groupProducts = (products, mapping) => {
  const groupedProducts = {};

  // Pre-initialize sections in mapping key order to preserve backend sorting
  Object.keys(mapping).forEach((section) => {
    groupedProducts[section] = [];
  });

  products.forEach((product) => {
    const productId = getProductUniqueId(product);

    const matchedSection = Object.keys(mapping).find((section) =>
      mapping[section]?.includes(productId)
    );

    if (matchedSection) {
      groupedProducts[matchedSection].push(product);
    }
  });

  // Remove empty sections
  Object.keys(groupedProducts).forEach((key) => {
    if (groupedProducts[key].length === 0) {
      delete groupedProducts[key];
    }
  });

  return groupedProducts;
};

export const getProductSection = (productId, mapping) => {
  return (
    Object.keys(mapping).find((section) =>
      mapping[section]?.includes(productId)
    ) || "Others"
  );
};

export const filterProducts = (groupedProducts, searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) return groupedProducts;

  const query = searchQuery.toLowerCase().trim();
  const filtered = {};

  Object.entries(groupedProducts).forEach(([section, products]) => {
    const sectionMatches = section.toLowerCase().includes(query);

    const matchingProducts = products.filter((product) => {
      const name = product?.name?.toLowerCase() || "";
      const shortName = product?.shortName?.toLowerCase() || "";
      return sectionMatches || name.includes(query) || shortName.includes(query);
    });

    if (matchingProducts.length > 0) {
      filtered[section] = matchingProducts;
    }
  });

  return filtered;
};

export const mapProductDetailsResponse = (raw) => {
  if (!raw) return null;

  const englishContent = (raw.productContent || []).filter((c) => c.lang === "en");
  const bySection = Object.fromEntries(englishContent.map((c) => [c.section, c]));
  const howItWorks = bySection.how_it_works;
  const ingredients = bySection.ingredients;
  const usage = (raw.productUsage || [])[0] || null;

  const sectionImage = (section) => (section?.productContentMedia || [])[0]?.url || null;

  const gallery = (raw.productImages || [])
    .filter((img) => img.context === "main" || img.context === "sub")
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((img) => ({ url: img.url, media_type: img.mediaType }));

  return {
    product_name: raw.name || "",
    description: raw.description || "",
    type: raw.type || "",
    product_image: raw.primaryImage || gallery[0]?.url || null,
    product_media: gallery,
    price: raw.price ? { amount: raw.price, currencyCode: raw.currencyCode } : null,
    quantity: raw.quantityAmount
      ? { amount: raw.quantityAmount, metric: raw.quantityMetric }
      : null,
    supporting_icons: (raw.productIcons || []).map((icon) => ({
      icon_url: icon.iconUrl,
      icon_title: icon.iconTitle,
    })),
    how_to_use: raw.videoLink
      ? {
          title: howItWorks?.title || "How to use?",
          description: howItWorks?.description || usage?.dosageInstructions || "",
          media: [{ desktop_url: raw.videoLink, thumbnail: raw.videoBanner }],
        }
      : null,
    course_duration: usage?.duration || "",
    dosage: usage?.dosageInstructions || raw.dosage || "",
    dosage_icons: (usage?.productUsageDosageIcons || []).map((d) => ({
      icon: d.icon,
      text: d.text,
    })),
    noticeable_changes: (usage?.productUsageNoticeableChanges || []).map((n) => ({
      icon: n.icon,
      text: n.type,
    })),
    key_ingredients: ingredients?.productContentItems?.length
      ? {
          title: "Key Ingredients",
          image: sectionImage(ingredients),
          items: ingredients.productContentItems.map((item) => ({
            title: item.title,
            description: item.description,
          })),
        }
      : null,
  };
};
